import { useMemo, useState, type ReactNode } from "react";
import Button from "@/components/base/Button";
import AdminPageHeader from "./AdminPageHeader";
import EmptyState from "./EmptyState";
import ResourceForm from "./ResourceForm";
import { useTableData } from "@/hooks/useTableData";
import { supabase } from "@/lib/supabase";
import { defaultValueFor, type AdminRow, type ColumnConfig, type FieldConfig } from "@/lib/adminFields";

interface ResourceManagerProps {
  table: string;
  title: string;
  description: string;
  fields: FieldConfig[];
  columns: ColumnConfig[];
  orderColumn?: string;
  itemSingular: string;
  itemPlural: string;
  emptyIcon: string;
  titleField: string;
  subtitleField?: string;
  imageField?: string;
  searchPlaceholder?: string;
  renderCell?: (row: AdminRow, column: ColumnConfig) => ReactNode;
}

export default function ResourceManager({
  table,
  title,
  description,
  fields,
  columns,
  orderColumn = "orden",
  itemSingular,
  itemPlural,
  emptyIcon,
  titleField,
  subtitleField,
  imageField,
  searchPlaceholder,
  renderCell,
}: ResourceManagerProps) {
  const { rows, loading, error, reload } = useTableData<AdminRow>(table, orderColumn);
  const [query, setQuery] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AdminRow | null>(null);
  const [confirmRow, setConfirmRow] = useState<AdminRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");

  const hasActivo = fields.some((field) => field.name === "activo");

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      [row[titleField], subtitleField ? row[subtitleField] : ""]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }, [rows, query, titleField, subtitleField]);

  const buildPayload = (values: Record<string, unknown>) => {
    const payload: Record<string, unknown> = {};
    fields.forEach((field) => {
      payload[field.name] = values[field.name] ?? defaultValueFor(field.type);
    });
    payload.actualizado_en = new Date().toISOString();
    return payload;
  };

  const openCreate = () => {
    setEditing(null);
    setActionError("");
    setFormOpen(true);
  };

  const openEdit = (row: AdminRow) => {
    setEditing(row);
    setActionError("");
    setFormOpen(true);
  };

  const handleSave = async (values: Record<string, unknown>) => {
    const payload = buildPayload(values);
    const { error: saveError } = editing
      ? await supabase.from(table).update(payload).eq("id", editing.id)
      : await supabase.from(table).insert(payload);
    if (saveError) throw saveError;
    setFormOpen(false);
    setEditing(null);
    await reload();
  };

  const handleDelete = async () => {
    if (!confirmRow) return;
    setBusyId(confirmRow.id);
    setActionError("");
    try {
      const { error: deleteError } = await supabase.from(table).delete().eq("id", confirmRow.id);
      if (deleteError) throw deleteError;
      await reload();
    } catch {
      setActionError(`No pudimos eliminar ${itemSingular.toLowerCase()}. Intenta nuevamente.`);
    } finally {
      setBusyId(null);
      setConfirmRow(null);
    }
  };

  const handleToggle = async (row: AdminRow) => {
    setBusyId(row.id);
    setActionError("");
    try {
      const { error: toggleError } = await supabase
        .from(table)
        .update({ activo: !row.activo, actualizado_en: new Date().toISOString() })
        .eq("id", row.id);
      if (toggleError) throw toggleError;
      await reload();
    } catch {
      setActionError("No pudimos cambiar la visibilidad. Intenta nuevamente.");
    } finally {
      setBusyId(null);
    }
  };

  const renderCellValue = (row: AdminRow, column: ColumnConfig) => {
    if (renderCell) {
      const custom = renderCell(row, column);
      if (custom !== undefined && custom !== null) return custom;
    }
    const value = row[column.name];
    if (column.type === "image") {
      return (
        <span className="flex h-11 w-16 items-center justify-center overflow-hidden rounded-md border border-background-200 bg-background-100">
          {value ? (
            <img src={String(value)} alt="" className="h-full w-full object-cover object-top" />
          ) : (
            <i className="ri-image-line text-base leading-none text-foreground-400" aria-hidden="true"></i>
          )}
        </span>
      );
    }
    if (column.type === "switch") {
      return (
        <button
          type="button"
          onClick={() => void handleToggle(row)}
          disabled={busyId === row.id}
          className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-label font-medium transition-colors disabled:opacity-50 ${
            value
              ? "border-primary-200 bg-primary-50 text-primary-800"
              : "border-background-300 bg-background-100 text-foreground-500"
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${value ? "bg-primary-500" : "bg-foreground-400"}`} aria-hidden="true"></span>
          {value ? "Visible" : "Oculto"}
        </button>
      );
    }
    if (column.type === "muted") {
      return <span className="text-sm text-foreground-500">{value ? String(value) : "—"}</span>;
    }
    return (
      <span className={`text-sm ${column.className ?? "font-medium text-foreground-900"}`}>
        {value ? String(value) : "—"}
      </span>
    );
  };

  return (
    <div>
      <AdminPageHeader
        title={title}
        description={description}
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="md" icon="ri-refresh-line" onClick={reload} disabled={loading}>
              Actualizar
            </Button>
            <Button variant="primary" size="md" icon="ri-add-line" onClick={openCreate}>
              Agregar
            </Button>
          </div>
        }
      />

      {actionError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
          {actionError}
        </div>
      )}

      {error ? (
        <div className="flex flex-col items-center rounded-lg border border-primary-200 bg-primary-50 px-6 py-10 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-wifi-off-line text-2xl leading-none" aria-hidden="true"></i>
          </span>
          <h3 className="mt-4 text-base font-heading font-semibold text-foreground-950">
            No se pudo cargar la información
          </h3>
          <p className="mt-1.5 max-w-md text-sm text-foreground-600">{error}</p>
          <Button variant="primary" size="md" icon="ri-refresh-line" onClick={reload} className="mt-5">
            Reintentar
          </Button>
        </div>
      ) : (
        <>
          {rows.length > 0 && (
            <div className="mb-4 relative">
              <i
                className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base leading-none text-foreground-400"
                aria-hidden="true"
              ></i>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder ?? `Buscar en ${itemPlural.toLowerCase()}…`}
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-800 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>
          )}

          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2, 3].map((key) => (
                <div key={key} className="h-16 animate-pulse rounded-lg border border-background-200 bg-background-100"></div>
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={emptyIcon}
              title={`Todavía no hay ${itemPlural.toLowerCase()}`}
              description={`Agrega ${itemSingular.toLowerCase()} para que aparezcan en el sitio público.`}
              action={
                <Button variant="primary" size="md" icon="ri-add-line" onClick={openCreate}>
                  Agregar {itemSingular.toLowerCase()}
                </Button>
              }
            />
          ) : (
            <>
              <div className="hidden overflow-hidden rounded-lg border border-background-200 bg-background-50 md:block">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-left">
                    <thead>
                      <tr className="border-b border-background-200 bg-background-100 text-xs font-label uppercase tracking-wide text-foreground-500">
                        {columns.map((column) => (
                          <th key={column.name} className="px-4 py-3 font-semibold">
                            {column.label}
                          </th>
                        ))}
                        <th className="px-4 py-3 text-right font-semibold">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-200">
                      {filtered.map((row) => (
                        <tr key={row.id} className="transition-colors hover:bg-background-100">
                          {columns.map((column) => (
                            <td key={column.name} className="px-4 py-3">
                              {renderCellValue(row, column)}
                            </td>
                          ))}
                          <td className="whitespace-nowrap px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => openEdit(row)}
                              aria-label={`Editar ${itemSingular.toLowerCase()}`}
                              className="mr-1 inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                            >
                              <i className="ri-pencil-line text-base leading-none" aria-hidden="true"></i>
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmRow(row)}
                              aria-label={`Eliminar ${itemSingular.toLowerCase()}`}
                              disabled={busyId === row.id}
                              className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:opacity-50"
                            >
                              <i className="ri-delete-bin-6-line text-base leading-none" aria-hidden="true"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <ul className="space-y-3 md:hidden">
                {filtered.map((row) => (
                  <li key={row.id} className="rounded-lg border border-background-200 bg-background-50 p-4">
                    <div className="flex items-start gap-3">
                      {imageField && (
                        <span className="flex h-16 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border border-background-200 bg-background-100">
                          {row[imageField] ? (
                            <img src={String(row[imageField])} alt="" className="h-full w-full object-cover object-top" />
                          ) : (
                            <i className="ri-image-line text-lg leading-none text-foreground-400" aria-hidden="true"></i>
                          )}
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-foreground-900">
                          {String(row[titleField] ?? "—")}
                        </p>
                        {subtitleField && (
                          <p className="mt-0.5 truncate text-xs text-foreground-500">
                            {String(row[subtitleField] ?? "")}
                          </p>
                        )}
                        {hasActivo && (
                          <div className="mt-2">{renderCellValue(row, { name: "activo", label: "Activo", type: "switch" })}</div>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm font-label font-medium text-foreground-700 transition-colors hover:bg-background-100"
                      >
                        <i className="ri-pencil-line text-base leading-none" aria-hidden="true"></i>
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmRow(row)}
                        className="flex cursor-pointer items-center justify-center rounded-md border border-background-300 px-3 py-2 text-sm font-label font-medium text-foreground-600 transition-colors hover:bg-background-100"
                        aria-label={`Eliminar ${itemSingular.toLowerCase()}`}
                      >
                        <i className="ri-delete-bin-6-line text-base leading-none" aria-hidden="true"></i>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-foreground-950/40 p-4 md:p-8">
          <div className="w-full max-w-2xl rounded-lg border border-background-200 bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
              <h2 className="text-base font-heading font-semibold text-foreground-950">
                {editing ? `Editar ${itemSingular.toLowerCase()}` : `Agregar ${itemSingular.toLowerCase()}`}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setFormOpen(false);
                  setEditing(null);
                }}
                aria-label="Cerrar"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
              >
                <i className="ri-close-line text-xl leading-none" aria-hidden="true"></i>
              </button>
            </div>
            <div className="p-5">
              <ResourceForm
                fields={fields}
                initial={editing ?? {}}
                submitLabel={editing ? "Guardar cambios" : "Crear"}
                onSubmit={handleSave}
                onCancel={() => {
                  setFormOpen(false);
                  setEditing(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {confirmRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/40 p-4">
          <div className="w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-6 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-delete-bin-6-line text-2xl leading-none" aria-hidden="true"></i>
            </span>
            <h3 className="mt-4 text-base font-heading font-semibold text-foreground-950">
              ¿Eliminar {itemSingular.toLowerCase()}?
            </h3>
            <p className="mt-1.5 text-sm text-foreground-600">
              Se quitará &quot;{String(confirmRow[titleField] ?? "")}&quot; de forma permanente.
            </p>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmRow(null)}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-label font-semibold text-foreground-700 transition-colors hover:bg-background-100"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={busyId === confirmRow.id}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-50 dark:text-foreground-950"
              >
                {busyId === confirmRow.id ? "Eliminando…" : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}